// src/components/admin/LogoUpload.tsx
import { useState, useRef, useEffect } from 'react';
import { unifiedApiService } from '../../services/unified';
import type { LogoInfo } from '../../services/unified/systemModule';
import { useToast } from '../../contexts/ToastContext';
import { env } from '../../config/env';

interface LogoUploadProps {
  onUploadSuccess: (logoUrl: string) => void;
}

export function LogoUpload({ onUploadSuccess }: LogoUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [logos, setLogos] = useState<LogoInfo[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showSuccess, showError } = useToast();

  // Helper to get full URL for logo
  const getLogoUrl = (url: string): string => {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `${env.API_BASE_URL}${url}`;
  };

  useEffect(() => {
    loadLogos();
  }, []);

  const loadLogos = async () => {
    try {
      setLoading(true);
      const logosList = await unifiedApiService.system.listLogos();
      setLogos(logosList);
    } catch (error) {
      console.error('Failed to load logos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (file: File) => {
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showError('File too large', 'Maximum file size is 5MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showError('Invalid file type', 'Please upload an image file');
      return;
    }

    // Upload file
    try {
      setUploading(true);
      const response = await unifiedApiService.system.uploadLogo(file);
      showSuccess('Logo uploaded successfully');
      onUploadSuccess(response.logo_url);
      await loadLogos(); // Reload logos list
    } catch (error) {
      showError('Upload failed', 'Failed to upload logo');
      console.error('Logo upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleActivate = async (filename: string) => {
    try {
      setUploading(true);
      await unifiedApiService.system.activateLogo(filename);
      showSuccess('Logo activated successfully');
      await loadLogos();
      // Find the activated logo and call onUploadSuccess
      const activatedLogo = logos.find(l => l.filename === filename);
      if (activatedLogo) {
        onUploadSuccess(activatedLogo.url);
      }
    } catch (error) {
      showError('Activation failed', 'Failed to activate logo');
      console.error('Logo activation error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (filename: string) => {
    if (!confirm('Are you sure you want to delete this logo?')) {
      return;
    }

    try {
      setUploading(true);
      await unifiedApiService.system.deleteLogoFile(filename);
      showSuccess('Logo deleted successfully');
      await loadLogos();
      // If deleted logo was active, clear it
      const deletedLogo = logos.find(l => l.filename === filename);
      if (deletedLogo?.is_active) {
        onUploadSuccess('');
      }
    } catch (error) {
      showError('Delete failed', 'Failed to delete logo');
      console.error('Logo delete error:', error);
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Company Logo
        </label>

        {/* Upload Area */}
        <div
          className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
            dragActive
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/*"
            onChange={handleChange}
            disabled={uploading}
          />

          <div className="text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="mt-4">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : 'Upload a logo'}
              </button>
              <p className="text-xs text-gray-500 mt-1">or drag and drop</p>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              PNG, JPG, GIF or WebP up to 5MB
            </p>
            <p className="text-xs text-gray-500">
              Recommended size: 200x50px
            </p>
          </div>
        </div>

        {/* Logos List */}
        {loading ? (
          <div className="mt-6 text-center text-gray-500">Loading logos...</div>
        ) : logos.length > 0 ? (
          <div className="mt-6">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Uploaded Logos</h4>
            <div className="space-y-2">
              {logos.map((logo) => (
                <div
                  key={logo.filename}
                  className={`flex items-center justify-between p-3 border rounded-lg ${
                    logo.is_active ? 'border-primary-500 bg-primary-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center space-x-3 flex-1">
                    <img
                      src={getLogoUrl(logo.url)}
                      alt="Logo"
                      className="h-12 w-12 object-contain border border-gray-200 rounded bg-white p-1"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {logo.filename}
                        {logo.is_active && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                            Active
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(logo.size)} • Uploaded {formatDate(logo.uploaded_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {!logo.is_active && (
                      <button
                        type="button"
                        onClick={() => handleActivate(logo.filename)}
                        disabled={uploading}
                        className="px-3 py-1 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded disabled:opacity-50"
                      >
                        Activate
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDelete(logo.filename)}
                      disabled={uploading}
                      className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded disabled:opacity-50"
                      title="Delete logo"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-6 text-center text-gray-500 text-sm">
            No logos uploaded yet
          </div>
        )}
      </div>
    </div>
  );
}
